import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import type { Ledger, LedgerType } from '@/api/types';

interface DeleteLedgerDialogProps {
    ledger: Ledger | null;
    type: LedgerType;
    open: boolean;
    onClose: () => void;
    onConfirm: (id: number) => Promise<void>;
    isLoading?: boolean;
}

/**
 * Confirmation dialog for deleting a ledger (asset or debt).
 */
export function DeleteLedgerDialog({
    ledger,
    type,
    open,
    onClose,
    onConfirm,
    isLoading = false,
}: DeleteLedgerDialogProps) {
    const typeLabel = type === 'asset' ? 'Asset' : 'Debt';

    const handleConfirm = async () => {
        if (ledger) {
            await onConfirm(ledger.id);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} title={`Delete ${typeLabel}`} size="sm">
            <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Are you sure you want to delete the {typeLabel.toLowerCase()}{' '}
                    <strong className="font-semibold text-gray-900 dark:text-gray-100">
                        {ledger?.label}
                    </strong>
                    ? This will also delete all associated value history. This action cannot be undone.
                </p>

                <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirm} isLoading={isLoading}>
                        Delete {typeLabel}
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
